<template>
  <el-drawer
    class="uf-drawer"
    :visible.sync="innerVisible"
    direction="rtl"
    size="800px"
    :with-header="false"
    :modal-append-to-body="true"
  >
    <div v-if="uf" class="drawer-wrap">
      <!-- 抽屉头部 -->
      <div class="drawer-header">
        <div class="dh-left">
          <div class="dh-uf">{{ uf }}</div>
          <div class="dh-text">
            <h3>{{ companyName }}</h3>
            <div class="dh-sub">
              <span>开票主体状态详情 · 双轨健康度</span>
              <span class="dh-sub-pt">Detalhes da entidade emissora · saúde das duas trilhas</span>
            </div>
          </div>
        </div>
        <span class="drawer-close" @click="innerVisible = false">×</span>
      </div>

      <div class="drawer-body">
        <!-- 综合状态 -->
        <div class="drawer-section">
          <span class="status-pill" :class="overall.cls">
            <span class="dot" />{{ overall.zh }}
            <span class="pill-pt">/ {{ overall.pt }}</span>
          </span>
          <div class="dual-track">
            <div class="track-card">
              <div class="tc-label">
                <span>税务资质（CNPJ/IE 接口）</span>
                <span class="tc-pt">Qualificação Fiscal (API CNPJ/IE)</span>
              </div>
              <div class="tc-status">
                <span class="status-pill" :class="taxLabel.cls">
                  <span class="dot" />{{ taxLabel.zh }}
                  <span class="pill-pt">/ {{ taxLabel.pt }}</span>
                </span>
              </div>
            </div>
            <div class="track-card">
              <div class="tc-label">
                <span>系统配置（CT-e/MDF-e 开票主体）</span>
                <span class="tc-pt">Config. Sistema (entidade emissora de CT-e/MDF-e)</span>
              </div>
              <div class="tc-status">
                <span class="status-pill" :class="sysLabel.cls">
                  <span class="dot" />{{ sysLabel.zh }}
                  <span class="pill-pt">/ {{ sysLabel.pt }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- 主体信息 -->
        <div class="drawer-section">
          <h4>
            <span>主体信息</span>
            <span class="h4-pt">Informações da Entidade</span>
          </h4>
          <div class="info-list">
            <div v-for="row in infoRows" :key="row.key" class="il-row">
              <div class="il-k">
                {{ row.label }}<span class="il-k-pt">{{ row.labelPt }}</span>
              </div>
              <div class="il-v" :class="{ num: row.mono }">
                <span v-if="row.key === 'expire' && expireBadge" class="expire-line">
                  <span>{{ row.value }}</span>
                  <span class="tag" :class="expireTagCls">{{ expireBadge }}</span>
                  <span class="il-v-pt">{{ row.valuePt }}</span>
                </span>
                <template v-else>
                  <span :class="{ 'is-empty': row.value === '—' }">{{ row.value }}</span>
                  <span v-if="row.valuePt" class="il-v-pt">{{ row.valuePt }}</span>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- 近期变更 -->
        <div class="drawer-section">
          <h4>
            <span>近期变更（来自变更日志）</span>
            <span class="h4-pt">Alterações recentes (do log de alterações)</span>
          </h4>
          <div v-for="(c, i) in recentChanges" :key="i" class="chg-mini">
            <div class="cm-top">
              <span>{{ chgTime(c) }}</span>
              <span>
                {{ chgTriggerZh(c) }}
                <span class="pt">/ {{ chgTriggerPt(c.triggerType) }}</span>
              </span>
            </div>
            <div v-for="(f, fi) in (c.changeFields || [])" :key="fi" class="cm-flow">
              <span class="cm-field">{{ f }}：</span>
              {{ valueOf(c.beforeValues, fi) }} → {{ valueOf(c.afterValues, fi) }}
            </div>
            <div v-if="c.operatorName" class="cm-op">
              操作人：{{ c.operatorName }}
              <span class="pt">/ Operador: {{ c.operatorName }}</span>
            </div>
          </div>
          <div v-if="!recentChanges.length" class="chg-mini">
            <div class="cm-flow is-empty">暂无变更记录<span class="pt">/ Nenhum registro de alteração</span></div>
          </div>
        </div>
      </div>

      <div class="drawer-footer">
        <el-button size="small" @click="innerVisible = false">
          <bi-label zh="关闭" pt="Fechar" inline />
        </el-button>
        <!-- 没有主体 id（如从地图点进无主体的州）时不允许发起单条查询 -->
        <el-button
          size="small" type="primary" :disabled="!queryId"
          :title="queryId ? '' : '该州无主体 ID，无法单条查询 / Sem ID da entidade'"
          @click="handleQuery"
        >
          <bi-label zh="单条查询" pt="Consultar" inline />
        </el-button>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import BiLabel from './BiLabel';
import {
  TAX_LABEL, SYS_LABEL, CHECK_SOURCE_LABEL, daysLeft, fmtCnpj
} from '../mock';
import { getInvoiceSubjectStatusMonitorIECheck } from '@/api/invoiceCenter';

/** 综合状态 overall：1正常 2警告 3异常 4停用 */
const OVERALL = {
  1: { zh: '正常 · 可开票', pt: 'Normal · pode emitir', cls: 'ok' },
  2: { zh: '预警 · 待确认', pt: 'Atenção · a confirmar', cls: 'warn' },
  3: { zh: '异常 · 不可开票', pt: 'Exceção · não pode emitir', cls: 'err' },
  4: { zh: '停用 · 未启用监控', pt: 'Desativado · monitoramento inativo', cls: 'warn' }
};

/** 状态最后变更原因：接口只给状态枚举，按税务 / 系统状态推导（中葡） */
const TAX_REASON = {
  2: { zh: 'IE 失效（被注销）', pt: 'IE Baixada (cancelada)' },
  3: { zh: '税务状态未知', pt: 'Status fiscal desconhecido' },
  4: { zh: '税务资质未录入', pt: 'Qualificação fiscal não registrada' }
};
const SYS_REASON = {
  2: { zh: '系统未配置开票主体', pt: 'Entidade emissora não configurada no sistema' }
};

export default {
  name: 'UfDetailDrawer',
  components: { BiLabel },
  props: {
    visible: { type: Boolean, default: false },
    uf: { type: String, default: '' },
    id: { type: Number, default: null },
    // 详情接口返回的单条记录：抽屉唯一数据源，缺失时不展示兜底数据
    detail: { type: Object, default: null },
    changeLog: { type: Array, default: () => [] }
  },
  computed: {
    /** 抽屉显隐：与父组件 visible 双向绑定（支持 v-model / .sync） */
    innerVisible: {
      get() { return this.visible; },
      set(v) { this.$emit('update:visible', v); }
    },
    /** 详情接口的记录（唯一展示来源）：没有 detail 时各字段一律显示占位符，不拼接默认值 */
    state() {
      return this.detail || {};
    },
    /** 同 state：字段同 IE 详情接口（companyName / enable / validade / daysLeft / lastCheckTime 等） */
    ie() {
      return this.detail || {};
    },
    /** 单条查询所需的 id：只有详情接口返回了 id 才允许查询 */
    queryId() {
      return (this.detail && this.detail.id) || this.id || null;
    },
    /** 公司名称：接口没给就显示占位符，不再拼默认公司名 */
    companyName() {
      return this.ie.companyName || '—';
    },
    /** 启用监控文案：enable 1 是 / 0 否；接口未返回时占位 */
    enableText() {
      const v = this.ie.enable;
      if (v === null || v === undefined || v === '') return { zh: '—', pt: '' };
      return Number(v) === 1 ? { zh: '是', pt: 'Sim' } : { zh: '否', pt: 'Não' };
    },
    /** 综合状态（overallStatus / overall：1正常 2警告 3异常 4停用）：中葡文案 + 配色 + 图标类名 */
    overall() {
      return OVERALL[this.statusOf(this.state, 'overallStatus', 'overall')] || { zh: '—', pt: '—', cls: 'pending' };
    },
    /** 税务状态标签（taxStatus / tax：1有效 2无效 3未知 4未录入）的中葡文案与配色 */
    taxLabel() {
      return TAX_LABEL[this.statusOf(this.state, 'taxStatus', 'tax')] || { zh: '—', pt: '—', cls: 'pending' };
    },
    /** 系统配置状态标签（sysStatus / sys：1已配置 2未配置）的中葡文案与配色 */
    sysLabel() {
      return SYS_LABEL[this.statusOf(this.state, 'sysStatus', 'sys')] || { zh: '—', pt: '—', cls: 'pending' };
    },
    /** 状态最后变更原因（中葡）：按税务 / 系统状态推导 */
    reason() {
      const tax = this.statusOf(this.state, 'taxStatus', 'tax');
      const sys = this.statusOf(this.state, 'sysStatus', 'sys');
      return TAX_REASON[tax] || SYS_REASON[sys] || { zh: '—', pt: '' };
    },
    /** 距有效期限的剩余天数：优先接口 daysLeft，缺失时按 validade 计算；未录入返回 null */
    expireDays() {
      if (typeof this.ie.daysLeft === 'number') return this.ie.daysLeft;
      return daysLeft(this.ie.validade);
    },
    /** 到期徽标中文文案：已过期 N 天 / 剩 N 天 */
    expireBadge() {
      const d = this.expireDays;
      if (d === null) return '';
      if (d < 0) return `已过期 ${Math.abs(d)} 天`;
      return `剩 ${d} 天`;
    },
    /** 到期徽标葡语文案：Vencido há N dias / N dias restantes */
    expireBadgePt() {
      const d = this.expireDays;
      if (d === null) return '';
      if (d < 0) return `Vencido há ${Math.abs(d)} dias`;
      return `${d} dias restantes`;
    },
    /** 到期徽标配色：已过期红 / 30 天内橙 / 其余绿 / 未录入灰 */
    expireTagCls() {
      const d = this.expireDays;
      if (d === null) return 'tag-gray';
      if (d < 0) return 'tag-err';
      if (d <= 30) return 'tag-warn';
      return 'tag-ok';
    },
    /** 地址拼接：街道 + 门牌号 + 补充信息 + 区 + 城市 + 邮编，缺项自动跳过 */
    address() {
      const { logradouro, numero, complemento, bairro, city, cep } = this.ie;
      const line1 = [logradouro, numero].filter(Boolean).join(', ');
      const line2 = [complemento, bairro].filter(Boolean).join(' · ');
      const line3 = [city, cep ? `CEP ${cep}` : ''].filter(Boolean).join(' · ');
      return [line1, line2, line3].filter(Boolean).join(' | ') || '—';
    },
    /** 抽屉「主体信息」区块的行数据（含中葡字段名，mono 表示等宽字体） */
    infoRows() {
      return [
        { key: 'uf', label: 'UF', labelPt: '', value: this.ie.uf || this.uf || '—' },
        { key: 'co', label: '公司名称', labelPt: 'Razão Social', value: this.companyName },
        { key: 'trade', label: '贸易名称', labelPt: 'Nome Fantasia', value: this.ie.tradeName || '—' },
        { key: 'cnpj', label: 'CNPJ', labelPt: '', value: fmtCnpj(this.ie.cnpj) || '—', mono: true },
        { key: 'ie', label: 'IE', labelPt: '', value: this.ie.ie || '—', mono: true },
        { key: 'phone', label: '电话', labelPt: 'Telefone', value: this.ie.phone || '—', mono: true },
        { key: 'addr', label: '地址', labelPt: 'Endereço', value: this.address },
        {
          key: 'enable',
          label: '启用监控',
          labelPt: 'Ativar Monitoramento',
          // enable：0停用 1启用；接口未返回时显示占位符
          value: this.enableText.zh,
          valuePt: this.enableText.pt
        },
        {
          key: 'expire',
          label: '预计到期时间',
          labelPt: 'Vencimento Previsto',
          value: this.fmtDate(this.ie.validade),
          valuePt: this.expireBadgePt ? `· ${this.expireBadgePt}` : '',
          mono: true
        },
        {
          key: 'reason',
          label: '状态最后变更',
          labelPt: 'Última Alteração de Status',
          value: this.reason.zh,
          valuePt: this.reason.pt ? `/ ${this.reason.pt}` : ''
        },
        {
          key: 'lastCheckTime',
          label: '最近检查时间',
          labelPt: 'Última Verificação',
          value: this.fmtTime(this.ie.lastCheckTime),
          valuePt: this.checkSourceZh(this.ie.lastCheckSource) ? `/ ${this.checkSourceZh(this.ie.lastCheckSource)}` : '',
          mono: true
        }
      ];
    },
    /** 该州的历史变更记录：按 UF 过滤；接口若未按州过滤（返回里没有本州记录），则原样展示全部 */
    recentChanges() {
      const list = Array.isArray(this.changeLog) ? this.changeLog : [];
      const mine = list.filter(c => c.uf === this.uf);
      return mine.length ? mine : list;
    }
  },
  methods: {
    /** 行内「查询」：触发该行所属州的单条查询 */
    handleQuery() {
      // 没有 id 直接忽略（按钮本身已 disabled，这里再兜一层）
      if (!this.queryId) return;
      const param = {
        id: this.queryId
      };
      getInvoiceSubjectStatusMonitorIECheck(param).then(res => {
        if (res.code === 200) {
          this.$message.success(`已触发 ${this.uf} 单条查询 / Consulta individual de ${this.uf} disparada`);
          this.$emit('check', this.uf);
        }
      });
    },
    /**
     * 时间 → MM-DD HH:mm:ss（最近检查时间展示）
     * 接口给的是 'yyyy-MM-dd HH:mm:ss'（非 ISO，Safari 无法解析），优先按字符串拆分，其它格式再走 Date
     */
    fmtTime(value) {
      if (!value) return '—';
      const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
      if (m) return `${m[2]}-${m[3]} ${m[4]}:${m[5]}:${m[6]}`;
      const d = new Date(value);
      if (isNaN(d.getTime())) return '—';
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
    /** 日期 → yyyy-MM-dd（validade 可能是 'yyyy-MM-dd HH:mm:ss'，只取日期部分） */
    fmtDate(value) {
      if (!value) return '—';
      const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
      return m ? `${m[1]}-${m[2]}-${m[3]}` : String(value);
    },
    /** 检查来源中文（lastCheckSource：1定时任务 / 2手动触发） */
    checkSourceZh(source) {
      return (CHECK_SOURCE_LABEL[source] || {}).zh || '';
    },
    /** 变更时间：优先毫秒时间戳 ts，其次 ISO 时间 time，格式化为 MM-DD HH:mm */
    chgTime(c) {
      const src = c.ts ? new Date(Number(c.ts)) : (c.time ? new Date(c.time) : null);
      if (!src || isNaN(src.getTime())) return c.time || '—';
      const p = n => String(n).padStart(2, '0');
      return `${p(src.getMonth() + 1)}-${p(src.getDate())} ${p(src.getHours())}:${p(src.getMinutes())}`;
    },
    /** 变更触发方式中文：接口返回 triggerTypeName 时直接用，否则按 triggerType 枚举翻译 */
    chgTriggerZh(c) {
      if (c && c.triggerTypeName) return c.triggerTypeName;
      return (CHECK_SOURCE_LABEL[c && c.triggerType] || {}).zh || '—';
    },
    /** 变更触发方式葡语 */
    chgTriggerPt(v) {
      return (CHECK_SOURCE_LABEL[v] || {}).pt || '';
    },
    /** 变更前 / 后取值：changeFields 与 beforeValues / afterValues 按同一顺序一一对应 */
    valueOf(list, index) {
      if (!Array.isArray(list)) return '—';
      const v = list[index];
      return v === null || v === undefined || v === '' ? '—' : v;
    },
    /** 状态值取值：详情接口用 overallStatus / taxStatus / sysStatus，地图接口用 overall / tax / sys */
    statusOf(obj, withSuffix, plain) {
      if (!obj) return null;
      const a = obj[withSuffix];
      if (a !== undefined && a !== null) return a;
      const b = obj[plain];
      return b === undefined ? null : b;
    }
  }
};
</script>

<style scoped lang="scss">
.drawer-wrap {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.drawer-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e6eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.drawer-header .dh-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.drawer-header .dh-uf {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #00a551;
  color: #fff;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  flex-shrink: 0;
}
.drawer-header .dh-text {
  min-width: 0;
}
.drawer-header h3 {
  font-size: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.drawer-header .dh-sub {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  color: #86909c;
}
.drawer-header .dh-sub-pt {
  font-size: 12px;
  color: #86909c;
}
.drawer-close {
  cursor: pointer;
  color: #86909c;
  font-size: 20px;
  line-height: 1;
  padding: 4px;
}
.drawer-close:hover { color: #1d2129; }

.drawer-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}
.drawer-section { margin-bottom: 20px; }
.drawer-section h4 {
  font-size: 13px;
  color: #4e5969;
  margin: 0 0 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.drawer-section h4::before {
  content: '';
  width: 3px;
  height: 13px;
  background: #00a551;
  border-radius: 2px;
  flex-shrink: 0;
}
.drawer-section h4 .h4-pt {
  font-size: 12px;
  font-weight: 400;
  color: #86909c;
}

.dual-track {
  display: flex;
  gap: 12px;
  margin-top: 12px;
}
.track-card {
  flex: 1;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 12px 14px;
  min-width: 0;
}
.track-card .tc-label {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  color: #86909c;
  margin-bottom: 8px;
}
.track-card .tc-label .tc-pt {
  font-size: 12px;
  color: #86909c;
}
.track-card .tc-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  flex-wrap: wrap;
}

.info-list {
  font-size: 13px;
}
.info-list .il-row {
  display: grid;
  grid-template-columns: 150px 1fr;
  column-gap: 12px;
  padding: 5px 0;
}
.info-list .il-k {
  color: #86909c;
  display: flex;
  flex-direction: column;
  font-size: 12px;
  line-height: 1.4;
}
.info-list .il-k-pt {
  font-size: 12px;
  color: #86909c;
}
.info-list .il-v {
  color: #1d2129;
  word-break: break-all;
}
.info-list .il-v .il-v-pt {
  display: block;
  font-size: 12px;
  color: #86909c;
  margin-top: 1px;
}
.info-list .il-v.num {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12px;
}
.info-list .il-v .is-empty { color: #c9cdd4; }
.expire-line {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.expire-line .il-v-pt {
  flex-basis: 100%;
}

.chg-mini {
  border: 1px solid #f0f1f5;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 12px;
}
.chg-mini + .chg-mini { margin-top: 8px; }
.chg-mini .cm-top {
  display: flex;
  justify-content: space-between;
  color: #86909c;
  margin-bottom: 4px;
}
.chg-mini .cm-flow { color: #1d2129; }
.chg-mini .cm-flow + .cm-flow { margin-top: 2px; }
.chg-mini .cm-flow.is-empty { color: #c9cdd4; }
.chg-mini .cm-field { color: #4e5969; }
.chg-mini .cm-op {
  margin-top: 4px;
  color: #86909c;
}
.chg-mini .pt {
  font-size: 12px;
  color: #86909c;
}
.chg-mini .cm-flow .pt {
  display: block;
  margin-top: 1px;
}

.drawer-footer {
  padding: 12px 20px;
  border-top: 1px solid #e5e6eb;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}
.status-pill.ok { background: rgba(0, 180, 42, 0.1); color: #00b42a; }
.status-pill.err { background: rgba(245, 63, 63, 0.1); color: #f53f3f; }
.status-pill.warn { background: rgba(255, 125, 0, 0.12); color: #ff7d00; }
.status-pill.pending { background: rgba(140, 145, 150, 0.14); color: #4e5969; }
.status-pill.gray { background: rgba(140, 145, 150, 0.14); color: #4e5969; }
.status-pill .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.status-pill .pill-pt {
  font-size: 12px;
  font-weight: 400;
}

.tag {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  line-height: 18px;
  white-space: nowrap;
}
.tag-ok { background: #e8f7e9; color: #00b42a; }
.tag-err { background: #ffecec; color: #f53f3f; }
.tag-warn { background: #fff3e0; color: #ff7d00; }
.tag-gray { background: #f2f3f5; color: #4e5969; }
</style>
